import { useState, useEffect, useCallback } from "react";
import { useLocalDraft } from "./useLocalDraft";
import { supabase } from "./supabaseclient";
import { detectJoint, JOINT_CONFIGS, MRC_OPTIONS } from "./utils/clinicalUtils";

// ─── THEME ────────────────────────────────────────────────────────────────────
const T = {
  bg: "#06090f", panel: "#0b0f1a", card: "#0f1623", border: "#1a2540",
  gold: "#c9a96e", goldLight: "#e8c98a", goldDim: "#7a6640",
  text: "#e8eaf0", textMid: "#7a8599", textDim: "#3d4a5c",
  active: "#10b981", activeBg: "#071f14", activeText: "#34d399",
  completedBg: "#13132e", completedText: "#a5b4fc", moderate: "#fbbf24",
  danger: "#f87171", dangerBg: "rgba(248,113,113,0.08)",
  purple: "#a78bfa", purpleBg: "rgba(167,139,250,0.08)",
};

const VARIABLE_TYPES = [
  { value: "number",  label: "Number (e.g. NPRS, ROM, weight)" },
  { value: "text",    label: "Text (e.g. diagnosis, remarks)" },
  { value: "select",  label: "Select (dropdown choices)" },
  { value: "boolean", label: "Yes / No" },
  { value: "date",    label: "Date" },
];

// ─── UI HELPERS ───────────────────────────────────────────────────────────────
function SectionLabel({ text }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.9px",
      textTransform: "uppercase", color: T.goldDim, marginBottom: 13 }}>
      {text}
    </div>
  );
}
function FieldLabel({ children }) {
  return (
    <label style={{ display: "block", fontSize: 11, fontWeight: 600,
      letterSpacing: "0.7px", textTransform: "uppercase", color: T.textDim, marginBottom: 5 }}>
      {children}
    </label>
  );
}
function Badge({ text, color, bg }) {
  return (
    <span style={{ fontSize: 10, padding: "2px 9px", borderRadius: 10,
      background: bg, color, fontWeight: 700, marginLeft: 6 }}>
      {text}
    </span>
  );
}

// ─── VARIABLE INTENT ─────────────────────────────────────────────────────────
function isRomVar(v) {
  const l = (v.label || "").toLowerCase();
  return v.unit === "°" || l.includes("rom") || l.includes("range of motion") ||
    l.includes("flexion") || l.includes("extension") || l.includes("rotation") ||
    l.includes("abduction") || l.includes("adduction") || l.includes("slr");
}
function isMmtVar(v) {
  const l = (v.label || "").toLowerCase();
  return l.includes("mmt") || l.includes("muscle") ||
    l.includes("grade") || l.includes("strength") || l.includes("power");
}
function isNprsVar(v) {
  const l = (v.label || "").toLowerCase();
  return l.includes("nprs") || l.includes("pain score") ||
    l.includes("vas") || l.includes("pain");
}

// ─── SESSION NOTE PARSER ──────────────────────────────────────────────────────
// Reads a SOAP note string saved by SOAPNoteAI and extracts structured values.
function parseSessionNote(noteText, jc) {
  const result = { rom: {}, mmt: {}, nprs: null, sessionNum: null };
  if (!noteText) return result;

  // Session number
  const snMatch = noteText.match(/Session\s*#?(\d+)/i);
  if (snMatch) result.sessionNum = parseInt(snMatch[1]);

  // NPRS
  const nprsMatch = noteText.match(/NPRS\s+(\d+)\s*\/\s*10/i) ||
                    noteText.match(/\(NPRS\s+(\d+)\s*\/\s*10\)/i) ||
                    noteText.match(/pain[:\s]+(\d+)\s*\/\s*10/i);
  if (nprsMatch) result.nprs = nprsMatch[1];

  if (!jc) return result;

  // ROM — match each movement by label keywords near a number
  jc.rom.forEach(r => {
    const words = r.label.replace(/[()°]/g, "").split(" ").filter(w => w.length > 3);
    for (const word of words) {
      const rx = new RegExp(word + "[^\\d\\n]{0,20}(\\d+\\.?\\d*)\\s*°?", "i");
      const m = noteText.match(rx);
      if (m) { result.rom[r.id] = m[1]; break; }
    }
  });

  // MMT — match each muscle by label keywords near "/5"
  jc.mmt.forEach(m => {
    const words = m.label.split("(")[0].trim().split(" ").filter(w => w.length > 3);
    for (const word of words) {
      const rx = new RegExp(word + "[^\\d\\n]{0,25}(\\d[+-]?)\\s*\\/\\s*5", "i");
      const match = noteText.match(rx);
      if (match) { result.mmt[m.id] = match[1]; break; }
    }
  });

  return result;
}

// ─── STATS ───────────────────────────────────────────────────────────────────
function calcStats(values) {
  const nums = values.map(Number).filter(n => !isNaN(n));
  if (!nums.length) return null;
  const mean = (nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(2);
  const sorted = [...nums].sort((a, b) => a - b);
  const median = nums.length % 2 === 0
    ? ((sorted[nums.length / 2 - 1] + sorted[nums.length / 2]) / 2).toFixed(2)
    : sorted[Math.floor(nums.length / 2)].toFixed(2);
  const sd = nums.length > 1
    ? Math.sqrt(nums.reduce((s, n) => s + Math.pow(n - mean, 2), 0) / (nums.length - 1)).toFixed(2)
    : "—";
  return { mean, median, sd, min: Math.min(...nums), max: Math.max(...nums), n: nums.length };
}

// ─── CSV EXPORT ───────────────────────────────────────────────────────────────
function exportToCSV(study, entries) {
  if (!entries.length) return;
  const joint = study.joint || detectJoint(study.title + " " + (study.description || ""));
  const jc = joint ? JOINT_CONFIGS[joint] : null;
  const vars = study.variables || [];

  const headers = ["Entry#", "Patient", "Session#", "Date", "NPRS"];
  const keys    = ["_idx",  "patient_name", "_session_num", "_date", "_nprs"];

  if (jc) {
    jc.rom.forEach(r => { headers.push(`ROM_${r.label}_(${r.unit})`); keys.push(`rom_${r.id}`); });
    jc.mmt.forEach(m => { headers.push(`MMT_${m.label.split("(")[0].trim()}`); keys.push(`mmt_${m.id}`); });
  }
  vars.filter(v => !isRomVar(v) && !isMmtVar(v) && !isNprsVar(v)).forEach(v => {
    headers.push(`${v.label}${v.unit ? "_(" + v.unit + ")" : ""}`);
    keys.push(v.id);
  });

  const rows = entries.map((e, i) => keys.map(k => {
    if (k === "_idx") return i + 1;
    if (k === "patient_name") return e.patient_name || "—";
    if (k === "_date") return new Date(e.created_at).toLocaleDateString("en-IN");
    return e.data?.[k] !== undefined && e.data[k] !== "" ? e.data[k] : "—";
  }));

  const csv = [headers, ...rows]
    .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${study.title.replace(/\s+/g, "_")}_research.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════
export default function ResearchModule({ session, patients, notify }) {
  const [view, setView]               = useState("list");
  const [studies, setStudies]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [activeStudy, setActiveStudy] = useState(null);
  const [entries, setEntries]         = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [studyView, setStudyView]     = useState("enroll");

  const [studyForm, setStudyForm, clearStudyForm] = useLocalDraft(
    "research_study_form", { title: "", description: "" }
  );
  const [variables, setVariables, clearVariables] = useLocalDraft("research_variables", []);
  const [savingStudy, setSavingStudy] = useState(false);
  const [newVar, setNewVar]           = useState({ label: "", type: "number", unit: "", options: "" });
  const [showVarForm, setShowVarForm] = useState(false);
  const [syncing, setSyncing]         = useState(false);
  const [syncLog, setSyncLog]         = useState([]);
  const [enrolling, setEnrolling]     = useState(false);

  useEffect(() => { fetchStudies(); }, []);

  async function fetchStudies() {
    setLoading(true);
    const { data, error } = await supabase
      .from("research_studies")
      .select("*")
      .eq("user_id", session.user.id)
      .order("created_at", { ascending: false });
    if (!error) setStudies(data || []);
    setLoading(false);
  }

  async function fetchEntries(studyId) {
    setLoadingEntries(true);
    const { data, error } = await supabase
      .from("research_entries")
      .select("*")
      .eq("study_id", studyId)
      .eq("user_id", session.user.id)
      .order("created_at", { ascending: false });
    if (!error) setEntries(data || []);
    setLoadingEntries(false);
  }

  async function saveStudy() {
    if (!studyForm.title.trim()) { notify("Study title is required", "error"); return; }
    if (!variables.length)       { notify("Add at least one variable", "error"); return; }
    setSavingStudy(true);
    const joint = detectJoint(studyForm.title + " " + studyForm.description);
    const { error } = await supabase.from("research_studies").insert([{
      user_id: session.user.id,
      title: studyForm.title.trim(),
      description: studyForm.description.trim(),
      variables,
      joint,
      enrolled_patients: [],
    }]);
    if (error) { notify("Save failed: " + error.message, "error"); setSavingStudy(false); return; }
    notify("Study created ✓");
    clearStudyForm(); clearVariables();
    setSavingStudy(false);
    setStudyForm({ title: "", description: "" });
    setVariables([]);
    setView("list");
    fetchStudies();
  }

  async function openStudy(study) {
    setActiveStudy(study);
    setView("study");
    setStudyView("enroll");
    setSyncLog([]);
    await fetchEntries(study.id);
  }

  async function toggleEnroll(patientId) {
    if (!activeStudy) return;
    setEnrolling(true);
    const current = activeStudy.enrolled_patients || [];
    const updated = current.includes(patientId)
      ? current.filter(id => id !== patientId)
      : [...current, patientId];
    const { error } = await supabase
      .from("research_studies")
      .update({ enrolled_patients: updated })
      .eq("id", activeStudy.id);
    if (error) { notify("Update failed: " + error.message, "error"); setEnrolling(false); return; }
    setActiveStudy(s => ({ ...s, enrolled_patients: updated }));
    setEnrolling(false);
  }

  // ── THE ENGINE: reads all enrolled patients' session history from Supabase,
  // parses each SOAP note for ROM/MMT/NPRS, deduplicates, bulk-inserts entries.
  async function syncAllSessions() {
    if (!activeStudy) return;
    const enrolled = activeStudy.enrolled_patients || [];
    if (!enrolled.length) { notify("Enroll at least one patient first", "error"); return; }

    setSyncing(true);
    setSyncLog([]);
    const log = [];

    const joint = activeStudy.joint ||
      detectJoint(activeStudy.title + " " + (activeStudy.description || ""));
    const jc   = joint ? JOINT_CONFIGS[joint] : null;
    const vars = activeStudy.variables || [];

    // 1. Fetch all enrolled patients at once
    const { data: patientData, error: pErr } = await supabase
      .from("patients")
      .select("id, name, condition, pain_score, notes, history, sessions, age, gender")
      .in("id", enrolled);

    if (pErr || !patientData) {
      notify("Failed to fetch patients: " + (pErr?.message || ""), "error");
      setSyncing(false);
      return;
    }

    // 2. Fetch existing entries to deduplicate
    const { data: existing } = await supabase
      .from("research_entries")
      .select("patient_id, data")
      .eq("study_id", activeStudy.id);

    const existingKeys = new Set(
      (existing || []).map(e => `${e.patient_id}_s${e.data?._session_num}`)
    );

    const toInsert = [];

    for (const patient of patientData) {
      // Gather all SOAP notes: notes field + history array
      const allNotes = [];
      if (patient.notes && patient.notes.includes("SOAP NOTE")) {
        allNotes.push(patient.notes);
      }
      if (Array.isArray(patient.history)) {
        patient.history
          .filter(h => typeof h === "string" && h.includes("SOAP NOTE"))
          .forEach(h => allNotes.push(h));
      }

      if (!allNotes.length) {
        log.push({ patient: patient.name, status: "skip", msg: "No SOAP notes found" });
        continue;
      }

      let newCount = 0;

      allNotes.forEach((noteText, idx) => {
        const parsed     = parseSessionNote(noteText, jc);
        const sessionNum = parsed.sessionNum || (idx + 1);
        const dedupeKey  = `${patient.id}_s${sessionNum}`;
        if (existingKeys.has(dedupeKey)) return;

        // Build entry data object
        const entryData = {
          _session_num:   sessionNum,
          _synced_from:   "session_notes",
          _sync_date:     new Date().toISOString(),
        };

        // NPRS — from parsed note or patient record
        const nprsVal = parsed.nprs ?? (patient.pain_score !== null ? String(patient.pain_score) : null);
        if (nprsVal !== null) {
          entryData._nprs = nprsVal;
          vars.filter(v => isNprsVar(v)).forEach(v => { entryData[v.id] = nprsVal; });
        }

        // ROM — one key per movement: rom_<id>
        if (jc) {
          jc.rom.forEach(r => {
            if (parsed.rom[r.id] !== undefined) {
              entryData[`rom_${r.id}`] = parsed.rom[r.id];
            }
          });
          // MMT — one key per muscle: mmt_<id>
          jc.mmt.forEach(m => {
            if (parsed.mmt[m.id] !== undefined) {
              entryData[`mmt_${m.id}`] = parsed.mmt[m.id];
            }
          });
        }

        // Demographic vars (age / gender) from patient record
        vars.forEach(v => {
          if (isRomVar(v) || isMmtVar(v) || isNprsVar(v)) return;
          const l = v.label.toLowerCase();
          if (l.includes("age")    && patient.age)    entryData[v.id] = String(patient.age);
          if (l.includes("gender") && patient.gender) entryData[v.id] = patient.gender;
        });

        toInsert.push({
          study_id:     activeStudy.id,
          user_id:      session.user.id,
          patient_id:   patient.id,
          patient_name: patient.name,
          data:         entryData,
        });
        existingKeys.add(dedupeKey);
        newCount++;
      });

      log.push({
        patient: patient.name,
        status:  newCount > 0 ? "ok" : "skip",
        msg:     newCount > 0
          ? `${newCount} new session${newCount > 1 ? "s" : ""} synced`
          : "All sessions already synced",
      });
    }

    // 3. Bulk insert
    if (toInsert.length) {
      const { error: iErr } = await supabase.from("research_entries").insert(toInsert);
      if (iErr) {
        notify("Insert error: " + iErr.message, "error");
        setSyncing(false);
        setSyncLog(log);
        return;
      }
    }

    const total = toInsert.length;
    notify(total > 0
      ? `✓ ${total} new session${total > 1 ? "s" : ""} synced into study`
      : "All sessions already up to date ✓"
    );
    setSyncLog(log);
    setSyncing(false);
    await fetchEntries(activeStudy.id);
  }

  async function deleteEntry(id) {
    if (!window.confirm("Delete this entry?")) return;
    await supabase.from("research_entries").delete().eq("id", id);
    notify("Entry deleted", "error");
    fetchEntries(activeStudy.id);
  }

  async function deleteStudy(id) {
    if (!window.confirm("Delete this study and ALL its data? Cannot be undone.")) return;
    await supabase.from("research_studies").delete().eq("id", id);
    notify("Study deleted", "error");
    fetchStudies();
  }

  function addVariable() {
    if (!newVar.label.trim()) { notify("Variable name is required", "error"); return; }
    setVariables(prev => [...prev, {
      id: `var_${Date.now()}`,
      label: newVar.label.trim(),
      type: newVar.type,
      unit: newVar.unit.trim(),
      options: newVar.type === "select"
        ? newVar.options.split(",").map(o => o.trim()).filter(Boolean) : [],
    }]);
    setNewVar({ label: "", type: "number", unit: "", options: "" });
    setShowVarForm(false);
  }
  function removeVariable(id) { setVariables(prev => prev.filter(v => v.id !== id)); }

  function getJc(study) {
    if (!study) return null;
    const j = study.joint || detectJoint(study.title + " " + (study.description || ""));
    return j ? JOINT_CONFIGS[j] : null;
  }

  // ════════════════════════════════════════════════════════════════════════
  // LIST VIEW
  // ════════════════════════════════════════════════════════════════════════
  if (view === "list") {
    return (
      <div style={{ padding: "30px 16px" }}>
        <div style={{ display: "flex", alignItems: "flex-start",
          justifyContent: "space-between", marginBottom: 28 }}>
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond',serif",
              fontSize: 32, fontWeight: 600, marginBottom: 4 }}>Research Module</div>
            <div style={{ color: T.textMid, fontSize: 13 }}>
              Enroll patients once · Sessions auto-sync from daily notes · Export for SPSS
            </div>
          </div>
          <button className="btn btn-gold" onClick={() => setView("create")}>+ New Study</button>
        </div>

        {loading ? (
          <div style={{ color: T.textDim, textAlign: "center", padding: 40 }}>Loading...</div>
        ) : studies.length === 0 ? (
          <div className="card" style={{ padding: 60, textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔬</div>
            <div style={{ fontFamily: "'Cormorant Garamond',serif",
              fontSize: 24, color: T.goldLight, marginBottom: 8 }}>No Research Studies Yet</div>
            <div style={{ fontSize: 13, color: T.textDim, marginBottom: 24,
              maxWidth: 480, margin: "0 auto 24px" }}>
              Create a study, enroll patients, and click Sync — their daily session
              data flows in automatically. Zero re-entry.
            </div>
            <button className="btn btn-gold" onClick={() => setView("create")}>
              + Create First Study
            </button>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
            {studies.map(study => {
              const jc       = getJc(study);
              const enrolled = (study.enrolled_patients || []).length;
              return (
                <div key={study.id} className="card"
                  style={{ padding: 24, borderTop: `2px solid ${T.gold}` }}>
                  <div style={{ fontFamily: "'Cormorant Garamond',serif",
                    fontSize: 18, fontWeight: 600, color: T.goldLight, marginBottom: 6 }}>
                    {study.title}
                  </div>
                  {study.description && (
                    <div style={{ fontSize: 12, color: T.textDim,
                      marginBottom: 12, lineHeight: 1.6 }}>{study.description}</div>
                  )}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                    <Badge text={`${(study.variables || []).length} vars`}
                      color={T.gold} bg="rgba(201,169,110,0.1)" />
                    {jc && <Badge text={jc.label}
                      color={T.activeText} bg="rgba(16,185,129,0.1)" />}
                    <Badge text={`${enrolled} enrolled`}
                      color={T.purple} bg={T.purpleBg} />
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="btn btn-gold btn-sm" style={{ flex: 1 }}
                      onClick={() => openStudy(study)}>Open</button>
                    <button className="btn btn-danger btn-sm"
                      onClick={() => deleteStudy(study.id)}>Delete</button>
                  </div>
                </div>
              );
            })}
            <div className="card row-hover"
              style={{ padding: 24, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                cursor: "pointer", border: `1px dashed ${T.border}`, minHeight: 180 }}
              onClick={() => setView("create")}>
              <div style={{ fontSize: 36, color: T.goldDim, marginBottom: 12 }}>+</div>
              <div style={{ fontSize: 13, color: T.textDim }}>New Research Study</div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // CREATE VIEW
  // ════════════════════════════════════════════════════════════════════════
  if (view === "create") {
    const previewJoint = detectJoint(studyForm.title + " " + studyForm.description);
    const previewJc    = previewJoint ? JOINT_CONFIGS[previewJoint] : null;

    return (
      <div style={{ padding: "30px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 28 }}>
          <button className="btn btn-outline btn-sm" onClick={() => setView("list")}>← Back</button>
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond',serif",
              fontSize: 28, fontWeight: 600 }}>Create Research Study</div>
            <div style={{ color: T.textMid, fontSize: 12, marginTop: 3 }}>
              Define variables → enroll patients → click Sync → data flows in automatically
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div className="card" style={{ padding: 24 }}>
            <SectionLabel text="Study Details" />
            <div style={{ marginBottom: 16 }}>
              <FieldLabel>Study Title *</FieldLabel>
              <input value={studyForm.title}
                onChange={e => setStudyForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Effect of TENS on Knee OA Pain" />
            </div>
            <div style={{ marginBottom: 20 }}>
              <FieldLabel>Description / Objective</FieldLabel>
              <textarea rows={3} value={studyForm.description}
                onChange={e => setStudyForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Brief description of objective, population, design..."
                style={{ resize: "vertical" }} />
            </div>

            {previewJc ? (
              <div style={{ padding: "12px 14px", borderRadius: 8, marginBottom: 20,
                background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.2)" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.activeText,
                  marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.6px" }}>
                  ✓ Auto-detected: {previewJc.label}
                </div>
                <div style={{ fontSize: 11, color: T.textDim, lineHeight: 1.8 }}>
                  <span style={{ color: T.textMid, fontWeight: 600 }}>ROM synced: </span>
                  {previewJc.rom.map(r => r.label).join(", ")}
                </div>
                <div style={{ fontSize: 11, color: T.textDim, lineHeight: 1.8, marginTop: 2 }}>
                  <span style={{ color: T.textMid, fontWeight: 600 }}>MMT synced: </span>
                  {previewJc.mmt.map(m => m.label.split("(")[0].trim()).join(", ")}
                </div>
                <div style={{ marginTop: 8, fontSize: 11, color: T.gold }}>
                  ⚡ All values pulled from daily SOAP notes automatically — no manual entry
                </div>
              </div>
            ) : (
              <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 20,
                background: T.panel, border: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 11, color: T.textDim }}>
                  Joint auto-detects as you type (knee, shoulder, lumbar, etc.)
                </div>
              </div>
            )}

            <SectionLabel text="Quick Add Variables" />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {[
                { label: "NPRS Score",      type: "number", unit: "/10" },
                { label: "ROM",             type: "number", unit: "°" },
                { label: "MMT Grade",       type: "number", unit: "/5" },
                { label: "VAS Score",       type: "number", unit: "/100" },
                { label: "Age",             type: "number", unit: "years" },
                { label: "Gender",          type: "select", options: "Male,Female,Other" },
                { label: "Treatment Group", type: "select", options: "Control,Experimental" },
                { label: "Intervention",    type: "select",
                  options: "Manual Therapy,Modalities,Exercise,Combined" },
                { label: "Remarks",         type: "text",   unit: "" },
              ].map(t => (
                <button key={t.label} className="btn btn-outline btn-sm" style={{ fontSize: 11 }}
                  onClick={() => {
                    if (variables.find(v => v.label === t.label)) return;
                    setVariables(prev => [...prev, {
                      id: `var_${Date.now()}_${Math.random()}`,
                      label: t.label, type: t.type, unit: t.unit || "",
                      options: t.options ? t.options.split(",") : [],
                    }]);
                  }}>
                  + {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="card" style={{ padding: 24 }}>
            <div style={{ display: "flex", alignItems: "center",
              justifyContent: "space-between", marginBottom: 13 }}>
              <SectionLabel text={`Variables (${variables.length})`} />
              <button className="btn btn-gold btn-sm"
                onClick={() => setShowVarForm(!showVarForm)}>
                {showVarForm ? "Cancel" : "+ Custom"}
              </button>
            </div>

            {showVarForm && (
              <div style={{ background: T.panel, borderRadius: 10, padding: 16,
                marginBottom: 16, border: `1px solid ${T.border}` }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
                  gap: 12, marginBottom: 12 }}>
                  <div>
                    <FieldLabel>Name *</FieldLabel>
                    <input value={newVar.label}
                      onChange={e => setNewVar(v => ({ ...v, label: e.target.value }))}
                      placeholder="e.g. Shoulder Flexion ROM" />
                  </div>
                  <div>
                    <FieldLabel>Type</FieldLabel>
                    <select value={newVar.type}
                      onChange={e => setNewVar(v => ({ ...v, type: e.target.value }))}>
                      {VARIABLE_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <FieldLabel>Unit</FieldLabel>
                    <input value={newVar.unit}
                      onChange={e => setNewVar(v => ({ ...v, unit: e.target.value }))}
                      placeholder="°, /10, kg..." />
                  </div>
                  {newVar.type === "select" && (
                    <div>
                      <FieldLabel>Options (comma separated)</FieldLabel>
                      <input value={newVar.options}
                        onChange={e => setNewVar(v => ({ ...v, options: e.target.value }))}
                        placeholder="Option1, Option2" />
                    </div>
                  )}
                </div>
                <button className="btn btn-gold btn-sm" onClick={addVariable}>Add</button>
              </div>
            )}

            {variables.length === 0 ? (
              <div style={{ color: T.textDim, fontSize: 13,
                textAlign: "center", padding: 24 }}>
                Use quick add or create a custom variable.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {variables.map((v, i) => (
                  <div key={v.id} style={{ display: "flex", alignItems: "center",
                    justifyContent: "space-between", padding: "10px 14px",
                    background: T.panel, borderRadius: 8, border: `1px solid ${T.border}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ width: 22, height: 22, borderRadius: "50%",
                        background: `${T.gold}22`, display: "flex", alignItems: "center",
                        justifyContent: "center", fontSize: 10, fontWeight: 700,
                        color: T.gold, flexShrink: 0 }}>{i + 1}</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
                          {v.label}
                          {v.unit && <span style={{ color: T.textDim, fontWeight: 400 }}>
                            {" "}({v.unit})</span>}
                          {isRomVar(v) && previewJc && (
                            <Badge text={`→ ${previewJc.rom.length} movements`}
                              color={T.activeText} bg="rgba(16,185,129,0.1)" />
                          )}
                          {isMmtVar(v) && previewJc && (
                            <Badge text={`→ ${previewJc.mmt.length} muscles`}
                              color={T.purple} bg={T.purpleBg} />
                          )}
                          {isNprsVar(v) && (
                            <Badge text="→ from pain score"
                              color={T.moderate} bg="rgba(251,191,36,0.1)" />
                          )}
                        </div>
                        <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>
                          {VARIABLE_TYPES.find(t => t.value === v.type)?.label || v.type}
                        </div>
                      </div>
                    </div>
                    <button onClick={() => removeVariable(v.id)}
                      style={{ background: "none", border: "none",
                        color: T.danger, cursor: "pointer", fontSize: 16, padding: "0 4px" }}>✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {variables.length > 0 && (
              <button className="btn btn-gold" style={{ width: "100%", marginTop: 20 }}
                onClick={saveStudy} disabled={savingStudy}>
                {savingStudy ? "Creating..." : "✓ Create Study"}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // STUDY VIEW
  // ════════════════════════════════════════════════════════════════════════
  if (view === "study" && activeStudy) {
    const vars            = activeStudy.variables || [];
    const jc              = getJc(activeStudy);
    const enrolled        = activeStudy.enrolled_patients || [];
    const enrolledPts     = patients.filter(p => enrolled.includes(p.id));
    const unenrolledPts   = patients.filter(p => !enrolled.includes(p.id));
    const romCols         = jc ? jc.rom : [];
    const mmtCols         = jc ? jc.mmt : [];
    const otherVars       = vars.filter(v => !isRomVar(v) && !isMmtVar(v) && !isNprsVar(v));

    return (
      <div style={{ padding: "30px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
          <button className="btn btn-outline btn-sm"
            onClick={() => { setView("list"); setActiveStudy(null); }}>← Back</button>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Cormorant Garamond',serif",
              fontSize: 26, fontWeight: 600 }}>{activeStudy.title}</div>
            <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap",
              alignItems: "center" }}>
              {activeStudy.description && (
                <span style={{ fontSize: 12, color: T.textMid }}>
                  {activeStudy.description}
                </span>
              )}
              {jc && <Badge text={jc.label} color={T.activeText} bg="rgba(16,185,129,0.1)" />}
              <Badge text={`${enrolled.length} enrolled`} color={T.purple} bg={T.purpleBg} />
            </div>
          </div>
          <button className="btn btn-outline btn-sm"
            onClick={() => exportToCSV(activeStudy, entries)} disabled={!entries.length}>
            📥 Export CSV ({entries.length})
          </button>
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 24 }}>
          {[
            { label: "Total Sessions", val: entries.length, col: T.gold },
            { label: "Enrolled",       val: enrolled.length, col: T.purple },
            { label: "Patients w/ Data",
              val: new Set(entries.filter(e => e.patient_id).map(e => e.patient_id)).size,
              col: T.activeText },
            { label: "Last Synced",
              val: entries[0] ? new Date(entries[0].created_at)
                .toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—",
              col: T.moderate },
          ].map(s => (
            <div key={s.label} style={{ background: T.card, border: `1px solid ${T.border}`,
              borderRadius: 10, padding: "16px 18px", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0,
                height: 2, background: s.col }} />
              <div style={{ fontSize: 10, color: T.textDim, fontWeight: 700,
                letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 6 }}>
                {s.label}
              </div>
              <div style={{ fontFamily: "'Cormorant Garamond',serif",
                fontSize: 28, fontWeight: 700, color: s.col }}>{s.val}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: `1px solid ${T.border}`, marginBottom: 24 }}>
          {[
            { id: "enroll", label: "👥 Patients & Sync" },
            { id: "data",   label: `📊 Data (${entries.length})` },
          ].map(t => (
            <div key={t.id} onClick={() => setStudyView(t.id)}
              style={{ cursor: "pointer", padding: "9px 18px", fontSize: 11,
                fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase",
                color: studyView === t.id ? T.gold : T.textMid,
                borderBottom: `2px solid ${studyView === t.id ? T.gold : "transparent"}`,
                transition: "all 0.18s" }}>
              {t.label}
            </div>
          ))}
        </div>

        {/* ── ENROLL + SYNC TAB ────────────────────────────────────────────── */}
        {studyView === "enroll" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            {/* LEFT: Enrolled + sync */}
            <div className="card" style={{ padding: 24 }}>
              <div style={{ display: "flex", alignItems: "center",
                justifyContent: "space-between", marginBottom: 14 }}>
                <SectionLabel text={`Enrolled (${enrolledPts.length})`} />
                <button className="btn btn-gold"
                  onClick={syncAllSessions}
                  disabled={syncing || !enrolledPts.length}
                  style={{ display: "flex", alignItems: "center",
                    gap: 8, padding: "10px 20px", fontSize: 13 }}>
                  {syncing ? "⏳ Syncing..." : "⚡ Sync All Sessions"}
                </button>
              </div>

              <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14,
                background: "rgba(201,169,110,0.05)", border: `1px solid ${T.border}`,
                fontSize: 11, color: T.textDim, lineHeight: 1.7 }}>
                <strong style={{ color: T.gold }}>How it works:</strong> Click Sync and the
                system reads every SOAP note saved for enrolled patients. It extracts ROM,
                MMT grades, and NPRS from each session and adds them to this study
                automatically. Run it anytime to pick up new sessions.
              </div>

              {enrolledPts.length === 0 ? (
                <div style={{ color: T.textDim, fontSize: 13,
                  textAlign: "center", padding: 24 }}>
                  No patients enrolled yet. Add from the list on the right.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 8 }}>
                  {enrolledPts.map(p => {
                    const sc = entries.filter(e => e.patient_id === p.id).length;
                    return (
                      <div key={p.id} style={{ display: "flex", alignItems: "center",
                        justifyContent: "space-between", padding: "12px 14px",
                        background: T.panel, borderRadius: 8, border: `1px solid ${T.border}` }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
                            {p.name}
                          </div>
                          <div style={{ fontSize: 11, color: T.textDim, marginTop: 2 }}>
                            {p.condition} ·{" "}
                            <span style={{ color: sc > 0 ? T.activeText : T.textDim }}>
                              {sc > 0 ? `${sc} session${sc > 1 ? "s" : ""} synced` : "not yet synced"}
                            </span>
                          </div>
                        </div>
                        <button onClick={() => toggleEnroll(p.id)} disabled={enrolling}
                          className="btn btn-danger btn-sm">Remove</button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Sync log */}
              {syncLog.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <SectionLabel text="Last Sync Log" />
                  <div style={{ display: "grid", gap: 6 }}>
                    {syncLog.map((l, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center",
                        gap: 10, fontSize: 12, padding: "6px 10px",
                        borderRadius: 6, background: T.panel }}>
                        <span style={{ fontSize: 10, fontWeight: 700,
                          color: l.status === "ok" ? T.activeText : T.textDim }}>
                          {l.status === "ok" ? "✓" : "—"}
                        </span>
                        <span style={{ color: T.text, fontWeight: 600 }}>{l.patient}</span>
                        <span style={{ color: T.textMid }}>{l.msg}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT: Available patients */}
            <div className="card" style={{ padding: 24 }}>
              <SectionLabel text="Add Patients to Study" />
              {unenrolledPts.length === 0 ? (
                <div style={{ color: T.textDim, fontSize: 13,
                  textAlign: "center", padding: 24 }}>All patients enrolled.</div>
              ) : (
                <div style={{ display: "grid", gap: 8 }}>
                  {unenrolledPts.map(p => (
                    <div key={p.id} style={{ display: "flex", alignItems: "center",
                      justifyContent: "space-between", padding: "12px 14px",
                      background: T.panel, borderRadius: 8, border: `1px solid ${T.border}` }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
                          {p.name}
                        </div>
                        <div style={{ fontSize: 11, color: T.textDim, marginTop: 2 }}>
                          {p.condition}
                          {p.pain_score != null && ` · NPRS ${p.pain_score}/10`}
                          {p.sessions != null && ` · ${p.sessions} sessions`}
                        </div>
                      </div>
                      <button onClick={() => toggleEnroll(p.id)} disabled={enrolling}
                        className="btn btn-gold btn-sm">+ Enroll</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── DATA TAB ─────────────────────────────────────────────────────── */}
        {studyView === "data" && (
          <div className="card" style={{ padding: 24 }}>
            <div style={{ display: "flex", alignItems: "center",
              justifyContent: "space-between", marginBottom: 16 }}>
              <SectionLabel text={`${entries.length} session records`} />
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-outline btn-sm"
                  onClick={() => setStudyView("enroll")}>⚡ Sync More</button>
                {entries.length > 0 && (
                  <button className="btn btn-gold btn-sm"
                    onClick={() => exportToCSV(activeStudy, entries)}>
                    📥 Export CSV
                  </button>
                )}
              </div>
            </div>

            {loadingEntries ? (
              <div style={{ color: T.textDim, textAlign: "center", padding: 24 }}>Loading...</div>
            ) : entries.length === 0 ? (
              <div style={{ color: T.textDim, textAlign: "center", padding: 40 }}>
                No data yet. Go to <strong style={{ color: T.gold }}>Patients & Sync</strong> tab
                and click ⚡ Sync All Sessions.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: T.panel }}>
                      {["#", "Patient", "Session", "Date"].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                      <th style={{ ...thStyle, color: T.moderate }}>NPRS /10</th>
                      {romCols.map(r => (
                        <th key={r.id} style={{ ...thStyle, color: T.activeText }}>
                          {r.label} {r.unit}
                        </th>
                      ))}
                      {mmtCols.map(m => (
                        <th key={m.id} style={{ ...thStyle, color: T.purple }}>
                          {m.label.split("(")[0].trim()}
                        </th>
                      ))}
                      {otherVars.map(v => (
                        <th key={v.id} style={thStyle}>
                          {v.label}{v.unit ? ` (${v.unit})` : ""}
                        </th>
                      ))}
                      <th style={thStyle}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry, i) => (
                      <tr key={entry.id}
                        style={{ borderBottom: `1px solid ${T.border}`,
                          background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                        <td style={{ ...tdStyle, color: T.gold, fontWeight: 700 }}>
                          {entries.length - i}
                        </td>
                        <td style={{ ...tdStyle, color: T.text, fontWeight: 600,
                          whiteSpace: "nowrap" }}>{entry.patient_name}</td>
                        <td style={{ ...tdStyle, color: T.gold }}>
                          #{entry.data?._session_num || "—"}
                        </td>
                        <td style={{ ...tdStyle, color: T.textDim, whiteSpace: "nowrap" }}>
                          {new Date(entry.created_at).toLocaleDateString("en-IN",
                            { day: "numeric", month: "short", year: "numeric" })}
                        </td>
                        <td style={{ ...tdStyle, color: T.moderate, fontWeight: 600 }}>
                          {entry.data?._nprs
                            ? `${entry.data._nprs}/10`
                            : <span style={{ color: T.textDim }}>—</span>}
                        </td>
                        {romCols.map(r => {
                          const val = entry.data?.[`rom_${r.id}`];
                          const low = val && parseFloat(r.normal) > 0 &&
                            parseFloat(val) < parseFloat(r.normal) * 0.8;
                          return (
                            <td key={r.id} style={{ ...tdStyle,
                              color: low ? T.danger : T.textMid, fontWeight: low ? 700 : 400 }}>
                              {val ? `${val}°` : <span style={{ color: T.textDim }}>—</span>}
                            </td>
                          );
                        })}
                        {mmtCols.map(m => {
                          const val  = entry.data?.[`mmt_${m.id}`];
                          const weak = val && ["0","1","2","3"].includes(String(val));
                          return (
                            <td key={m.id} style={{ ...tdStyle,
                              color: weak ? T.moderate : T.textMid, fontWeight: weak ? 700 : 400 }}>
                              {val ? `${val}/5` : <span style={{ color: T.textDim }}>—</span>}
                            </td>
                          );
                        })}
                        {otherVars.map(v => (
                          <td key={v.id} style={tdStyle}>
                            {entry.data?.[v.id] || <span style={{ color: T.textDim }}>—</span>}
                          </td>
                        ))}
                        <td style={tdStyle}>
                          <button className="btn btn-danger btn-sm"
                            onClick={() => deleteEntry(entry.id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Stats */}
            {entries.length > 1 && (
              <div style={{ marginTop: 28 }}>
                <div style={{ height: 1, background: T.border, marginBottom: 20 }} />
                <SectionLabel text="Descriptive Statistics" />

                {/* NPRS */}
                {(() => {
                  const vals = entries.map(e => parseFloat(e.data?._nprs)).filter(n => !isNaN(n));
                  const s = calcStats(vals);
                  return s ? (
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 11, color: T.moderate, fontWeight: 700,
                        marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Pain Score
                      </div>
                      <div style={{ display: "grid",
                        gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                        <StatCard label="NPRS" unit="/10" stats={s} color={T.moderate} />
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* ROM */}
                {romCols.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 11, color: T.activeText, fontWeight: 700,
                      marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Range of Motion
                    </div>
                    <div style={{ display: "grid",
                      gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                      {romCols.map(r => {
                        const vals = entries
                          .map(e => parseFloat(e.data?.[`rom_${r.id}`])).filter(n => !isNaN(n));
                        const s = calcStats(vals);
                        return s ? <StatCard key={r.id} label={r.label} unit={r.unit}
                          stats={s} normal={parseFloat(r.normal)} color={T.activeText} /> : null;
                      })}
                    </div>
                  </div>
                )}

                {/* MMT */}
                {mmtCols.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 11, color: T.purple, fontWeight: 700,
                      marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Manual Muscle Testing
                    </div>
                    <div style={{ display: "grid",
                      gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                      {mmtCols.map(m => {
                        const vals = entries
                          .map(e => {
                            const v = e.data?.[`mmt_${m.id}`];
                            return v ? parseFloat(String(v).replace(/[+-]/g,"")) : NaN;
                          }).filter(n => !isNaN(n));
                        const s = calcStats(vals);
                        return s ? <StatCard key={m.id}
                          label={m.label.split("(")[0].trim()}
                          unit="/5" stats={s} normal={5} color={T.purple} /> : null;
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
}

// ─── STAT CARD ────────────────────────────────────────────────────────────────
function StatCard({ label, unit, stats, normal, color }) {
  const low = normal && parseFloat(stats.mean) < normal * 0.8;
  return (
    <div style={{ background: "#0b0f1a", borderRadius: 10,
      padding: 16, border: "1px solid #1a2540" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color, marginBottom: 10,
        display: "flex", alignItems: "center", gap: 6 }}>
        {label}{unit && ` (${unit})`}
        {low && (
          <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 6,
            background: "rgba(248,113,113,0.12)", color: "#f87171", fontWeight: 700 }}>
            MEAN LOW
          </span>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {[["Mean",stats.mean],["Median",stats.median],
          ["SD",stats.sd],["Min",stats.min],["Max",stats.max],["n",stats.n]].map(([l,v]) => (
          <div key={l}>
            <div style={{ fontSize: 9, color: "#3d4a5c",
              textTransform: "uppercase", letterSpacing: "0.5px" }}>{l}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#e8eaf0" }}>{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const thStyle = {
  padding: "10px 14px", textAlign: "left", fontSize: 9, fontWeight: 700,
  letterSpacing: "1px", textTransform: "uppercase", color: "#3d4a5c",
  borderBottom: "1px solid #1a2540", whiteSpace: "nowrap",
};
const tdStyle = { padding: "12px 14px", color: "#7a8599" };